import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);
  const logoRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/settings");
    setS(data);
  };
  useEffect(() => { load(); }, []);

  if (!s) return <div className="text-slate-500">Loading...</div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const uploadFile = async (file, endpoint, key) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, data[key]);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold">Configuration</div>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">Company Settings</h1>
        <p className="text-slate-600 mt-1">These details, logo and signature appear on all salary slips and reports.</p>
      </div>

      <Card className="border-[#E5E2DC] p-6 space-y-4">
        <h3 className="font-serif text-xl text-slate-900">Company Information</h3>
        <Field label="Company Name"><Input value={s.name} onChange={(e) => set("name", e.target.value)} data-testid="setting-name" /></Field>
        <Field label="Address"><Input value={s.address} onChange={(e) => set("address", e.target.value)} data-testid="setting-address" /></Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Phone"><Input value={s.phone} onChange={(e) => set("phone", e.target.value)} data-testid="setting-phone" /></Field>
          <Field label="Email"><Input value={s.email} onChange={(e) => set("email", e.target.value)} data-testid="setting-email" /></Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Authorized Signatory Name"><Input value={s.signatory_name} onChange={(e) => set("signatory_name", e.target.value)} data-testid="setting-signatory" /></Field>
          <Field label="Signatory Title"><Input value={s.signatory_title} onChange={(e) => set("signatory_title", e.target.value)} data-testid="setting-title" /></Field>
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="bg-[#0F1D42] hover:bg-[#C59B27] hover:text-black transition-colors text-white">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </Card>

      <Card className="border-[#E5E2DC] p-6">
        <h3 className="font-serif text-xl text-slate-900">Brand Assets</h3>
        <p className="text-sm text-slate-600 mt-1">Upload your logo and authorized signature image — used on all generated PDFs.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="border border-[#E5E2DC] rounded-lg p-5 bg-white">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Company Logo</div>
            <div className="mt-3 h-32 bg-[#FDFDF9] rounded flex items-center justify-center border border-[#E5E2DC]">
              {s.logo_url ? (
                <img src={`${BACKEND_URL}${s.logo_url}`} alt="Logo" className="max-h-28 object-contain" data-testid="settings-logo-preview" />
              ) : <div className="text-slate-400 text-sm">No logo</div>}
            </div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "/settings/logo", "logo_url")} data-testid="settings-logo-input" />
            <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => logoRef.current?.click()} data-testid="upload-logo-button"><Upload className="w-3 h-3 mr-1" /> Change Logo</Button>
          </div>
          <div className="border border-[#E5E2DC] rounded-lg p-5 bg-white">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Authorized Signature</div>
            <div className="mt-3 h-32 bg-[#FDFDF9] rounded flex items-center justify-center border border-[#E5E2DC]">
              {s.signature_url ? (
                <img src={`${BACKEND_URL}${s.signature_url}`} alt="Signature" className="max-h-28 object-contain" data-testid="settings-signature-preview" />
              ) : <div className="text-slate-400 text-sm">No signature uploaded</div>}
            </div>
            <input ref={sigRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "/settings/signature", "signature_url")} data-testid="settings-signature-input" />
            <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => sigRef.current?.click()} data-testid="upload-signature-button"><Upload className="w-3 h-3 mr-1" /> Upload Signature</Button>
            <div className="text-[11px] text-slate-500 mt-2">Tip: A transparent PNG signature scan works best.</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</Label>
    {children}
  </div>
);
